import { API_BASE_URL } from './config';
import { apiFetch } from './apiFetch';

export type CompetencyGap = {
  code: string;
  name: string;
  currentLevel: number;
  targetLevel: number;
  severity: 'low' | 'medium' | 'high';
};

export type IDPObjective = {
  competency: string;
  goal: string;
  resources: string[];
  timeline: string;
};

export type IDPMilestone = {
  label: string;
  dueDate: string;
  status: 'pending' | 'in-progress' | 'completed';
};

export type IDPPlan = {
  id: string;
  companyId: string;
  userId: string;
  generatedAt: string;
  status: 'draft' | 'active' | 'completed';
  competencyGaps: CompetencyGap[];
  objectives: IDPObjective[];
  milestones: IDPMilestone[];
  rawLlmOutput: string | null;
  readyForAdvancement?: boolean; // true when all competencies meet or exceed position requirements
  notes?: string | null;         // HR/manager notes visible to employee (migration 012)
  lastEditedAt?: string | null;  // when the plan was last manually edited (migration 012)
  createdAt: string;
  updatedAt: string;
};

export type IDPReadinessItem = {
  category: 'position' | 'competency_requirements' | 'competency_scores' | 'metrics' | 'training_catalogue' | 'milestone_templates';
  severity: 'blocking' | 'warning';
  message: string;
  action: string;
};

export type IDPReadinessResult = {
  ready: boolean;
  items: IDPReadinessItem[];
};

export async function getIDPReadiness(userId: string, companyId: string) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/idp/readiness/${userId}?companyId=${companyId}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{ success: boolean; data?: IDPReadinessResult; error?: string }>;
}

/** @deprecated Use startIDPGeneration() instead — this blocks until LLM responds */
export async function generateIDP(userId: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/idp/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, companyId }),
  });
  return res.json() as Promise<{
    success: boolean;
    skipped?: boolean;
    reason?: string;
    message?: string;
    data?: { plan: IDPPlan };
    error?: string;
  }>;
}

/**
 * Enqueue an async IDP generation job via the AI queue.
 * Returns immediately with a jobId — poll via processAIJob(jobId).
 */
export async function startIDPGeneration(userId: string, companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/ai-queue/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'idp', payload: { userId, companyId } }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { job: { id: string; status: string } };
    code?: string;
    error?: string;
  }>;
}

export async function getIDPsForUser(userId: string, companyId: string) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/idp/user/${userId}?companyId=${companyId}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{ success: boolean; data?: { plans: IDPPlan[] }; error?: string }>;
}

export async function listAllIDPs(companyId: string) {
  const res = await apiFetch(`${API_BASE_URL}/api/idp/company/${companyId}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  return res.json() as Promise<{ success: boolean; data?: { plans: IDPPlan[] }; error?: string }>;
}

export async function updateIDPStatus(
  idpId: string,
  companyId: string,
  status: IDPPlan['status'],
) {
  const res = await apiFetch(`${API_BASE_URL}/api/idp/${idpId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, status }),
  });
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

/**
 * Partial content update — HR/manager can edit objectives, milestones, and notes.
 * Employee can update their own milestone statuses.
 */
export async function updateIDPContent(
  idpId: string,
  companyId: string,
  editedByUserId: string,
  planOwnerUserId: string,
  patch: {
    objectives?: IDPObjective[];
    milestones?: IDPMilestone[];
    notes?: string | null;
  },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/idp/${idpId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, editedByUserId, planOwnerUserId, ...patch }),
  });
  return res.json() as Promise<{ success: boolean; data?: { plan: IDPPlan }; error?: string }>;
}

/**
 * Convenience wrapper: update a single milestone's status.
 * Can be called by the plan owner (employee) or HR/manager.
 */
export async function updateMilestoneStatus(
  idpId: string,
  companyId: string,
  callerId: string,
  planOwnerUserId: string,
  milestones: IDPMilestone[],
) {
  return updateIDPContent(idpId, companyId, callerId, planOwnerUserId, { milestones });
}
